import { defineStore } from "pinia";
import { Library, emptyLibrary } from "~/entities/Library";
import { LibraryContent, emptyLibraryContent, addLibraryContentSortId } from "~/entities/LibraryContent";
import type { ILibraryRepository } from "~/interfaces/ILibraryRepository";
import { LibraryRepository } from "~/repositories/LibraryRepository";
import { useContentsStore } from "~/stores/ContentsStore";
import { useUserStore } from "~/stores/UserStore";
import { usePlanStore } from "~/stores/PlanStore";
import { useLoginStore } from "~/stores/LoginStore";
import type { IContentsRepository } from "~/interfaces/IContentsRepository";
import { ContentsRepository } from "~/repositories/ContentsRepository";

/**
 * セレクションデータを扱うクラス
 */
export const useLibraryStore = defineStore("LibraryStore", () => {
  const libraries = ref<Library[]>([]);
  const selectedPlanId = ref<string>("");
  const selectedLibrary = ref<Library>(emptyLibrary());
  const editingLibrary = ref<Library>(emptyLibrary());
  const editingContents = ref<LibraryContent[]>([]);
  const editingContentsBackup = ref<LibraryContent[]>([]);
  // 編集中のフォルダ
  const editingFolder = ref<LibraryContent>();

  const loading = ref(false);
  const loadingUnread = ref(false);

  const repository: ILibraryRepository = new LibraryRepository();
  const contentsRepository: IContentsRepository = new ContentsRepository();

  const contentsStore = useContentsStore();
  const userStore = useUserStore();
  const loginStore = useLoginStore();
  const planStore = usePlanStore();

  // 未読管理用のライブラリリスト
  const unreadLibraryList = ref<string[]>([]);
  const hasUnreadLibrary = ref(false);

  // プランタブの未読フラグ
  const hasUnreadForPlanTab = computed(() => {
    return hasUnreadLibrary.value;
  });

  const fetchLibraries = async (planId: string) => {
    loading.value = true;
    libraries.value = [];
    try {
      libraries.value = await repository.getLibrariesWithUserInfo(planId, loginStore.loginInfo.userId);
    } catch (error: any) {
      sentryErrorLog("fetchLibraries", error);
    } finally {
      loading.value = false;
    }
  };

  // contentsとdirectoryに分ける
  const divideContents = (libraryContents: LibraryContent[]) => {
    const directories: LibraryContent[] = [];
    const contents: LibraryContent[] = [];

    let index = 0;
    // contentsとdirectoryに分ける
    libraryContents.forEach(content => {
      content.displayOrder = index;
      index++;
      if (content.isDirectory) {
        content.directoryContents.forEach(c => {
          c.displayOrder = index;
          index++;
        });
        directories.push(content);
      } else {
        contents.push(content);
      }
    });
    return { contents, directories };
  };

  // contentsリストを整理する
  const prepareContentsList = (contentsList: LibraryContent[]) => {
    if (!contentsList) {
      return;
    }
    // directoryにはIDがないので、IDを付与する
    let directoryId = 0;
    contentsList.forEach(content => {
      if (content.isDirectory) {
        content.id = directoryId;
        content.tempChildren = [];
        directoryId++;
      } else {
        content.tempChildren = [];
        content.directoryContents = [];
      }
      addLibraryContentSortId(content);
    });
  };

  // contentsリストをマージする
  // params: contentsList1: マージ先のリスト, contentsList2: マージ元のリスト、excludeEmptyDirectory: ディレクトリが空の場合は削除するかどうか
  const mergeContentsList = (
    contents1: LibraryContent[],
    contents2: LibraryContent[],
    excludeEmptyDirectory: boolean = false,
  ) => {
    let contentsList1 = contents1 ? [...contents1] : [];
    let contentsList2 = contents2 ? [...contents2] : [];
    if (excludeEmptyDirectory) {
      // 空のディレクトリは削除する
      contentsList1 = contentsList1.filter(content => {
        if (content.isDirectory && (content.directoryContents ? content.directoryContents.length : 0) === 0) {
          return false;
        }
        return true;
      });
      contentsList2 = contentsList2.filter(content => {
        if (content.isDirectory && (content.directoryContents ? content.directoryContents.length : 0) === 0) {
          return false;
        }
        return true;
      });
    }

    // 同じIDのコンテンツがあったらdirectoryContentsに追加する
    // 同じIDのコンテンツがなかったらcontentsに追加する
    contentsList2.forEach(content2 => {
      const content1 = contentsList1.find(content => content.id === content2.id);
      if (content1) {
        if (content1.isDirectory) {
          const directoryContent = content1.directoryContents.find(c => c.id === content2.id);
          if (!directoryContent) {
            // content1.directoryContentsにcontent2.directoryContentsを追加する
            content2.directoryContents.forEach(directoryContent => {
              content1.directoryContents.push(directoryContent);
            });
          }
        } else {
          contentsList1.push(content2);
        }
      } else {
        contentsList1.push(content2);
      }
    });
    // contentsList1をdisplayOrderでソートする
    contentsList1.sort((a, b) => {
      if (a.displayOrder === undefined || b.displayOrder === undefined) {
        return 0;
      }
      return a.displayOrder - b.displayOrder;
    });
    return contentsList1;
  };

  const getContentsProgress = async (libraryContents: LibraryContent[]) => {
    // すべてのコンテンツIDを収集
    const collectContentIds = (contents: LibraryContent[]): string[] => {
      return contents.reduce((ids: string[], content) => {
        if (content.isDirectory && content.directoryContents) {
          return [...ids, ...collectContentIds(content.directoryContents)];
        }
        return [...ids, String(content.id)];
      }, []);
    };

    // 進捗情報を一括取得
    const contentIds = collectContentIds(libraryContents);

    // 早期リターン: コンテンツIDが空の場合は処理をスキップ
    if (contentIds.length === 0) {
      return;
    }

    try {
      const userId = String(userStore.user?.userId);
      const allUserContentsInfo = await contentsStore.getUserContentsListAll(userId, contentIds);
      const userContentsInfoMap = new Map(allUserContentsInfo.map(info => [info.id, info]));

      // プラン制限情報を一括取得
      const planId = String(planStore.plan?.id);
      const allPlanContentsInfo = await contentsRepository.getPlanContentsListByIdsAll(planId, contentIds);
      const planContentsInfoMap = new Map(allPlanContentsInfo.map(info => [info.contentsId.toString(), info]));

      // ユーザーのプラン情報を取得
      const userPlanPriceId = planStore.plan?.contractStatus?.planPriceId;

      const isOwner = planStore.plan?.isOwner;

      // 進捗を計算して設定
      const setProgress = (contents: LibraryContent[]) => {
        for (const content of contents) {
          if (content.isDirectory) {
            if (content.directoryContents && content.directoryContents.length > 0) {
              setProgress(content.directoryContents);
            }
          } else {
            const contentInfo = userContentsInfoMap.get(String(content.id));
            if (content.contentsType === "article") {
              content.progress = content.isComplete ? 100 : 0;
            } else if (content.contentsType === "document") {
              content.progress = Math.floor((contentInfo?.maxPage / content.documentTotalPage) * 100);
            } else if (["movie", "archive_movie"].includes(content.contentsType)) {
              if (content.isComplete) {
                content.progress = 100;
              } else {
                content.progress = Math.floor((contentInfo?.maxTime / content.moviePlayTimeSec) * 100);
              }
            }
            if (content.progress > 100) content.progress = 100;
            if (content.progress < 0) content.progress = 0;
            if (content.progress === undefined) content.progress = 0;
            if (Number.isNaN(content.progress)) content.progress = 0;

            // プラン制限の判定
            const planContentsInfo = planContentsInfoMap.get(String(content.id));
            content.isLimitedByPlanPrice = isContentLimitedByPlanPrice(
              planContentsInfo?.limitedPlanPriceIds,
              userPlanPriceId,
              isOwner ?? false,
            );
          }
        }
      };

      setProgress(libraryContents);
    } catch (error) {
      sentryErrorLog("getContentsProgress", error);
      // エラー時はプラン制限情報を付与せずに処理を継続
    }
  };

  const getDirectoryProgress = (libraryContents: LibraryContent[]) => {
    for (const content of libraryContents) {
      if (content.isDirectory) {
        // ディレクトリの中のコンテンツのComplete状態を取得する
        if (content.directoryContents && content.directoryContents.length > 0) {
          let progress = 0;
          let isComplete = true;
          content.directoryContents.forEach(c => {
            if (c.isComplete) {
              progress += 100;
            } else {
              isComplete = false;
              progress += c.progress;
            }
            c.directoryContents = [];
            c.tempChildren = [];
          });
          progress = Math.floor(progress / content.directoryContents.length);
          content.progress = progress;
          content.isComplete = isComplete;
        } else {
          content.progress = 0;
          content.isComplete = false;
        }
      }
    }
  };

  const getLibrary = async (libraryId: string, page: number, pageSize: number) => {
    const response = await repository.getLibrary(libraryId, page, pageSize);
    prepareContentsList(response.contents);
    return response;
  };

  const createLibrary = async (planId: string, library: Library, libraryContents: LibraryContent[]) => {
    const { contents, directories } = divideContents(libraryContents);
    return await repository.createLibrary(planId, library, contents, directories);
  };

  const updateLibrary = async (planId: string, library: Library, libraryContents: LibraryContent[]) => {
    const { contents, directories } = divideContents(libraryContents);
    return await repository.updateLibrary(planId, library, contents, directories);
  };

  const deleteLibrary = async (planId: string, libraryId: string) => {
    return await repository.deleteLibrary(planId, libraryId);
  };

  const browseLibrary = async (libraryId: string) => {
    return await repository.browseLibrary(libraryId, loginStore.loginInfo.userId);
  };

  const updateDisplaySort = async (planId: string, libraryIds: string[]) => {
    return await repository.updateDisplaySort(planId, libraryIds);
  };

  const startEdit = (library?: Library, contents?: LibraryContent[]) => {
    editingLibrary.value = library ?? emptyLibrary();
    editingContents.value = contents ?? [];
    editingFolder.value = undefined;
  };

  const startEditFolder = (content?: LibraryContent) => {
    if (content) {
      editingFolder.value = content;
      editingFolder.value.isEdit = true;
    } else {
      editingFolder.value = emptyLibraryContent();
      editingFolder.value.isDirectory = true;
      editingFolder.value.isEdit = false;
      //  editingContents.valueの中にあるフォルダのIDの最大値を取得する
      const maxId = editingContents.value.reduce((max, content) => {
        if (content.isDirectory && content.id > max) {
          return content.id;
        }
        return max;
      }, 0);

      editingFolder.value.id = maxId + 1;
      addLibraryContentSortId(editingFolder.value);
    }
  };

  const endEditFolder = () => {
    editingFolder.value = undefined;
  };

  const backupEditingContents = () => {
    editingContentsBackup.value = JSON.parse(JSON.stringify(editingContents.value));
  };
  const restoreEditingContents = () => {
    editingContents.value = [...editingContentsBackup.value];
    editingContentsBackup.value = [];
  };

  // プランごとのライブラリ未読情報を取得
  const getPlanLibraryUnread = async (planId: string) => {
    if (!loginStore.isLogin()) {
      return;
    }
    loadingUnread.value = true;
    try {
      const unreadList = await repository.getPlanLibraryUnreadInfo(planId, loginStore.loginInfo.userId);
      unreadLibraryList.value = unreadList;
      hasUnreadLibrary.value = unreadList.length > 0;
    } finally {
      loadingUnread.value = false;
    }
  };

  const clearPlanLibraryUnread = () => {
    unreadLibraryList.value = [];
    hasUnreadLibrary.value = false;
  };

  return {
    loading,
    libraries,
    selectedPlanId,
    selectedLibrary,
    editingLibrary,
    editingContents,
    editingFolder,
    mergeContentsList,
    getContentsProgress,
    getDirectoryProgress,
    fetchLibraries,
    getLibrary,
    createLibrary,
    updateLibrary,
    updateDisplaySort,
    deleteLibrary,
    browseLibrary,
    startEdit,
    startEditFolder,
    endEditFolder,
    backupEditingContents,
    restoreEditingContents,
    getPlanLibraryUnread,
    unreadLibraryList,
    hasUnreadLibrary,
    hasUnreadForPlanTab,
    clearPlanLibraryUnread,
  };
});
